import axios from 'axios';
import { apiClient, getApiBaseUrl } from '../core/api/apiClient';
import type { AlumniProfile } from './alumni.service';

export interface CauseInput {
  cause_name: string;
  category: string;
  sub_category?: string;
  summary?: string;
}

export interface PredictionFilters {
  locations?: string[];
  batches?: string[];
  included_vals_dimensions?: string[];
}

export interface DonorPredictionRequest {
  cause: CauseInput;
  donor_type?: 'all' | 'existing' | 'new';
  filters?: PredictionFilters;
  final_weights?: {
    alignment: number;
    philanthropic: number;
    capacity: number;
  };
  philanthropic_weights?: {
    philanthropic_history: number;
    cumulative_giving: number;
    category_affinity: number;
  };
  capacity_weights?: {
    net_worth: number;
    propensity: number;
  };
}

export interface RankedDonor {
  rank: number;
  donor_id: string;
  name: string;
  final_score: number;
  alignment_score: number;
  philanthropic_score: number;
  capacity_score: number;
  primary_motivation?: string;
  likely_causes?: string[];
  recommended_ask_inr?: string;
  outreach_strategy?: string;
  match_reason: string;
  // Custom frontend extension for full details matching
  _full_profile?: AlumniProfile;
}

export interface DonorPredictionResponse {
  cause_name: string;
  cause_vals?: Record<string, number>;
  scoring_rationale?: string;
  ranked_donors: RankedDonor[];
  total_donors_evaluated: number;
}

export interface DonorPredictionHistoryResponse {
  history: CauseInput[];
}

export interface GlobalFrequentDonor {
  donor_id: string;
  name: string;
  global_score: number;
  appearance_count: number;
}

export interface GlobalFrequentDonorsResponse {
  donors: GlobalFrequentDonor[];
}

// ─────────────────────────────────────────────────────────────────────────────
// V3 Two-Head LambdaRank Types
// ─────────────────────────────────────────────────────────────────────────────

export interface V3DonorPredictionRequest {
  project_id?: string;
  cause_name: string;
  category: string;
  sub_category?: string;
  objective?: string;
  keywords?: string;
  total_amount?: number;
  top_k?: number;
  include_sensitive_financials?: boolean;
}

export interface FinancialIndicators {
  cumulative_giving: number;
  net_worth_usd_millions?: number | null;
  recommended_ask_inr?: number | null;
}

export interface V3RankedAlumni {
  rank: number;
  donor_id: string;
  name: string;
  rank_score: number;
  batch_year?: number | null;
  department?: string;
  company?: string;
  primary_motivation?: string;
  interests?: string;
  retrieval_cosine: number;
  financial_indicators?: FinancialIndicators;
  // Frontend extension — enriched full profile
  _full_profile?: AlumniProfile;
}

export interface V3RecsysCounts {
  candidates_retrieved_existing: number;
  candidates_retrieved_prospects: number;
  recommended_existing: number;
  recommended_prospects: number;
}

export interface V3DonorPredictionResponse {
  status: string;
  project_id: string;
  cause_name: string;
  category: string;
  total_amount: number;
  configured_top_k: number;
  counts: V3RecsysCounts;
  existing_donors: V3RankedAlumni[];
  new_prospects: V3RankedAlumni[];
  latency_ms: number;
}

export const donorPredictionService = {
  rankDonorsHeuristic: (
    request: DonorPredictionRequest,
    onProgress?: (current: number, total: number, message?: string) => void,
    isRetry: boolean = false
  ): Promise<DonorPredictionResponse> => {
    return new Promise((resolve, reject) => {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = import.meta.env.DEV ? 'localhost:8000' : window.location.host;
      
      const basePath = (import.meta.env.VITE_BASE_PATH || '/').replace(/\/$/, '');
      const wsUrl = import.meta.env.DEV
        ? `${wsProtocol}//${wsHost}/api/donor-prediction/rank`
        : `${wsProtocol}//${wsHost}${basePath}/api/donor-prediction/rank`;
      const ws = new WebSocket(wsUrl);
      let completed = false;

      ws.onopen = () => {
        ws.send(JSON.stringify(request));
      };

      ws.onmessage = async (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'progress') {
            if (onProgress) {
              onProgress(payload.current, payload.total, payload.message);
            }
          } else if (payload.type === 'result') {
            completed = true;
            resolve(payload.data);
            ws.close();
          } else if (payload.type === 'error') {
            completed = true;
            ws.close();
            
            if (payload.message === 'Authentication required' && !isRetry) {
              try {
                await axios.post(`${getApiBaseUrl()}/auth/refresh`, {}, { withCredentials: true });
                const retryResult = await donorPredictionService.rankDonorsHeuristic(request, onProgress, true);
                resolve(retryResult);
              } catch (refreshErr) {
                localStorage.removeItem('token');
                localStorage.removeItem('name');
                localStorage.removeItem('role');
                if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
                }
                reject(new Error('Authentication expired. Please log in again.'));
              }
            } else {
              reject(new Error(payload.message || 'Prediction failed'));
            }
          }
        } catch (err) {
          completed = true;
          reject(err);
          ws.close();
        }
      };

      ws.onerror = () => {
        if (!completed) {
          completed = true;
          reject(new Error('WebSocket connection error'));
        }
      };

      ws.onclose = () => {
        if (!completed) {
          completed = true;
          reject(new Error('WebSocket connection closed prematurely'));
        }
      };
    });
  },

  rankDonorsV3: async (request: V3DonorPredictionRequest): Promise<V3DonorPredictionResponse> => {
    return apiClient<V3DonorPredictionResponse>('/v3/donor-prediction/recommend', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  getPredictionHistory: async (): Promise<DonorPredictionHistoryResponse> => {
    return apiClient<DonorPredictionHistoryResponse>('/donor-prediction/history', {
      method: 'GET',
    });
  },

  exportDonorsLightGBM: async (donor_ids: string[]): Promise<Blob> => {
    return apiClient<Blob>('/v1/exports/donors', {
      method: 'POST',
      body: JSON.stringify({ donor_ids }),
      responseType: 'blob',
    });
  },

  getGlobalFrequentDonors: async (): Promise<GlobalFrequentDonorsResponse> => {
    return apiClient<GlobalFrequentDonorsResponse>('/donor-prediction/global-top', {
      method: 'GET',
    });
  },

  precheckTraining: async (request: TrainingPrecheckRequest = {}): Promise<TrainingPrecheckResponse> => {
    return apiClient<TrainingPrecheckResponse>('/v2/donor-prediction/train/precheck', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  startTraining: async (request: TrainModelRequest = {}): Promise<TrainModelResponse> => {
    return apiClient<TrainModelResponse>('/v2/donor-prediction/train', {
      method: 'POST',
      body: JSON.stringify({
        ...request,
        auto_activate: false, // strictly enforce no auto-activation
      }),
    });
  },

  getTrainingStatus: async (jobId: string): Promise<TrainingStatusResponse> => {
    return apiClient<TrainingStatusResponse>(`/v2/donor-prediction/train/status/${jobId}`, {
      method: 'GET',
    });
  },

  listModels: async (): Promise<ModelListResponse> => {
    return apiClient<ModelListResponse>('/v2/donor-prediction/models', {
      method: 'GET',
    });
  },

  activateModel: async (versionId: string): Promise<ActivateModelResponse> => {
    return apiClient<ActivateModelResponse>(`/v2/donor-prediction/models/${encodeURIComponent(versionId)}/activate`, {
      method: 'POST',
    });
  },
};

export interface TrainingDatabaseBounds {
  earliest_donation_date?: string;
  latest_donation_date?: string;
  earliest_project_date?: string;
  latest_project_date?: string;
  total_donations?: number;
  total_projects?: number;
  resolved_projects?: number;
  source?: string;
}

export interface TrainingPrecheckRequest {
  from_date?: string;
  to_date?: string;
}

export interface TrainingPrecheckResponse {
  from_date?: string;
  to_date?: string;
  projects: number;
  donations: number;
  positive_outcomes: number;
  head_a_candidate_estimate?: number;
  head_b_candidate_estimate?: number;
  can_train: boolean;
  warning?: string | null;
  database_bounds?: TrainingDatabaseBounds;
}

export interface TrainModelRequest {
  from_date?: string;
  to_date?: string;
  description?: string;
  auto_activate?: boolean;
}

export interface TrainModelResponse {
  job_id: string;
  status: string;
  message: string;
  created_at: string;
  from_date?: string;
  to_date?: string;
  database_date_range?: TrainingDatabaseBounds;
}

export interface HeadEvaluationMetrics {
  ndcg_10: number;
  ndcg_50: number;
  hit_10: number;
  mrr: number;
  evaluated_groups?: number;
}

export interface ComparativeHeadMetrics {
  head_a: HeadEvaluationMetrics;
  head_b: HeadEvaluationMetrics;
}

export interface DeltaHeadMetrics {
  head_a: {
    ndcg_10: number;
    ndcg_50: number;
    hit_10: number;
    mrr: number;
  };
  head_b: {
    ndcg_10: number;
    ndcg_50: number;
    hit_10: number;
    mrr: number;
  };
}

export interface TrainingMetrics {
  candidate?: ComparativeHeadMetrics;
  champion?: ComparativeHeadMetrics;
  delta_vs_champion?: DeltaHeadMetrics;
  // Fallbacks for legacy/flat payloads
  head_a?: HeadEvaluationMetrics;
  head_b?: HeadEvaluationMetrics;
}

export interface TrainingStatusResponse {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress_pct: number;
  stage: string;
  message?: string;
  from_date?: string;
  to_date?: string;
  trained_by?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  elapsed_seconds?: number;
  version_id?: string;
  metrics?: TrainingMetrics;
  error?: string | null;
}

export interface ModelVersionSummary {
  version_id: string;
  from_date?: string;
  to_date?: string;
  training_range?: {
    from_date: string;
    to_date: string;
  } | null;
  created_at?: string;
  trained_by?: string;
  description?: string;
  is_baseline: boolean;
  is_active: boolean;
  metrics?: TrainingMetrics;
  data_stats?: Record<string, any>;
  status?: string;
  parent_version?: string;
  training_type?: string;
}

export interface ModelListResponse {
  active_version: string;
  active_model_info?: Record<string, any>;
  models: ModelVersionSummary[];
  total_versions: number;
}

export interface ActivateModelResponse {
  status: string;
  active_version: string;
  message: string;
}


